/**
 * Utilitário puro pra capturar a tela atual e retornar base64.
 *
 * <p>Extraído da lógica original do {@code ScreenshotService} (que fazia
 * capture + upload REST) pra permitir reuso pelo {@code RemoteOpsService}
 * (que envia via WebSocket). Capture é o ÚNICO pedaço compartilhado;
 * cada caller decide o que fazer com o resultado.</p>
 *
 * <p>Estratégia híbrida (Solução A - PLAYER_KIOSK_REMOTE_VIEW):</p>
 * <ul>
 *   <li>{@code html2canvas} captura DOM/HTML/imagens (com {@code ignoreElements}
 *       filtrando &lt;video&gt; — esses são tratados separadamente).</li>
 *   <li>Cada &lt;video&gt; visível é composto via {@code ctx.drawImage(videoEl)},
 *       que faz readback do GPU overlay sob demanda (resolve preto em Tizen/webOS
 *       modernos onde {@code html2canvas} falhava).</li>
 *   <li>Aspect ratio preservado: replica {@code object-fit: cover} via versão
 *       9-param do {@code drawImage} com crop central.</li>
 *   <li>Z-index respeitado: usa {@code globalCompositeOperation: 'destination-over'}
 *       pra desenhar o vídeo POR BAIXO dos overlays já capturados.</li>
 *   <li>Quando {@code drawImage} falha (DRM, TVs muito antigas, frame não
 *       disponível): desenha placeholder informativo em vez de deixar preto.</li>
 * </ul>
 *
 * <p>Mitigações de plataforma:</p>
 * <ul>
 *   <li>Captura só o {@code #stream-container} (ou fallbacks) — não fullscreen,
 *       pra reduzir memória em TVs.</li>
 *   <li>JPEG quality 0.7 — equilibra qualidade × tamanho.</li>
 *   <li>Escala pra max 1280×720 — Smart TV friendly.</li>
 *   <li>{@code allowTaint: false} — preserva canvas exportável; depende de
 *       CORS configurado no R2 + {@code crossOrigin} no &lt;video&gt;.</li>
 * </ul>
 *
 * <p><b>Limitações conhecidas:</b></p>
 * <ul>
 *   <li>Vídeo com DRM (Widevine, PlayReady): {@code drawImage} lança
 *       {@code SecurityError} por design — placeholder informativo aparece.</li>
 *   <li>Tizen &lt; 4.0 / webOS &lt; 4.0: engines antigos podem não fazer readback
 *       — placeholder informativo aparece.</li>
 *   <li>Frame capturado pode estar 1-2 frames atrasado vs o panel (latência
 *       inerente ao readback GPU).</li>
 * </ul>
 */
import html2canvas from 'html2canvas';
import {
  probeCapturePlatformCapabilities,
  shouldSkipTizenVideoReadback,
  shouldTryDisplayMediaCapture,
  tryCaptureViaDisplayMedia,
} from './capturePlatform';
import {
  composeLogicalSnapshotFullFrame,
  isVideoOnScreen,
  resolveVideoContentId,
  type PlaybackContext,
} from './captureLogicalFallback';

import {
  findScaledViewportContent,
  neutralizeScaledViewportInClone,
} from './scaledViewport';

// Nota: PlaybackContext é exportado diretamente por captureLogicalFallback.ts —
// o re-export aqui era duplicado e quebrava `export *` no src/utils/index.ts
// com "Multiple exports of name 'PlaybackContext'" (eslint import/export).
// Consumers devem importar de './captureLogicalFallback' ou de '~/utils'.

/** Posição de um filho no canvas — layout quando há ScaledViewport (transform quebra html2canvas). */
function layoutDestInCaptureCanvas(
  target: HTMLElement,
  el: HTMLElement,
  renderScale: number,
): { destX: number; destY: number; destW: number; destH: number } {
  if (findScaledViewportContent(target)) {
    const destW = Math.max(0, el.offsetWidth * renderScale);
    const destH = Math.max(0, el.offsetHeight * renderScale);
    if (destW <= 0 || destH <= 0) {
      return { destX: 0, destY: 0, destW: 0, destH: 0 };
    }

    let node: HTMLElement | null = el;
    let offsetX = 0;
    let offsetY = 0;
    while (node && node !== target) {
      offsetX += node.offsetLeft;
      offsetY += node.offsetTop;
      const parent: Element | null = node.offsetParent;
      node = parent instanceof HTMLElement ? parent : null;
    }

    return {
      destX: offsetX * renderScale,
      destY: offsetY * renderScale,
      destW,
      destH,
    };
  }

  const targetRect = target.getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  return {
    destX: (rect.left - targetRect.left) * renderScale,
    destY: (rect.top - targetRect.top) * renderScale,
    destW: rect.width * renderScale,
    destH: rect.height * renderScale,
  };
}

function videoDestInCaptureCanvas(
  target: HTMLElement,
  video: HTMLVideoElement,
  renderScale: number,
): { destX: number; destY: number; destW: number; destH: number } {
  return layoutDestInCaptureCanvas(target, video, renderScale);
}

const MAX_WIDTH = 1280;
const MAX_HEIGHT = 720;
const JPEG_QUALITY = 0.7;

/**
 * Tizen (Samsung) Consumer TVs renderizam o {@code <video>} num HW overlay
 * plane separado do compositor HTML. Tentar capturar o frame de vídeo
 * (ImageCapture / WebGL texImage2D / rVFC / drawImage + getImageData) é:
 *   (a) inútil — o readback sempre volta preto (overlay vazio); e
 *   (b) o gatilho do crash do app inteiro — o readback nativo do overlay
 *       mata o processo, SEM estouro de heap JS.
 *
 * <p>Diagnóstico ao vivo (2026-05-29) na TV UN50U8600FGXZD / Tizen 9.0:
 * o último log antes da morte silenciosa do app foi exatamente
 * {@code "video[0] drawVideo start (1920x1080)"} — heap estável em 15/347MB.
 * Logo, em Tizen pulamos toda a cadeia de captura de vídeo e desenhamos
 * apenas o placeholder informativo (DOM continua sendo capturado normal).</p>
 */
function isTizenPlatform(): boolean {
  try {
    if (import.meta.env?.VITE_PLATFORM === 'tizen') return true;
  } catch {
    /* import.meta pode não existir em certos ambientes de teste/SSR */
  }
  return (
    typeof window !== 'undefined' &&
    typeof (window as { tizen?: unknown }).tizen !== 'undefined'
  );
}

/**
 * Hardening: teto de tempo pra captura inteira. Mesmo que {@code html2canvas}
 * (ou qualquer etapa) trave numa TV, a captura resolve com {@code canvas:null}
 * e o {@code captureMutex} é liberado no {@code finally} — evita deixar todas
 * as capturas seguintes penduradas eternamente atrás de um mutex que nunca
 * resolve.
 */
const CAPTURE_TIMEOUT_MS = 8000;

function emptyDiagnostics(
  targetId: string,
  durationMs: number,
): CaptureDiagnostics {
  return {
    targetId,
    targetTag: targetId,
    targetRect: { w: 0, h: 0 },
    renderScale: 0,
    domCanvasOk: false,
    domCanvasDims: null,
    videoCount: 0,
    videos: [],
    durationMs,
  };
}

function withCaptureTimeout(
  p: Promise<{
    canvas: HTMLCanvasElement | null;
    diagnostics: CaptureDiagnostics;
  }>,
): Promise<{
  canvas: HTMLCanvasElement | null;
  diagnostics: CaptureDiagnostics;
}> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      console.warn(
        `[captureScreen] captura excedeu ${CAPTURE_TIMEOUT_MS}ms — abortando (resultado nulo)`,
      );
      resolve({
        canvas: null,
        diagnostics: emptyDiagnostics('(timeout)', CAPTURE_TIMEOUT_MS),
      });
    }, CAPTURE_TIMEOUT_MS);

    p.then((r) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(r);
    }).catch((err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      console.error('[captureScreen] captura rejeitou:', err);
      resolve({ canvas: null, diagnostics: emptyDiagnostics('(error)', 0) });
    });
  });
}

export interface ScreenCapture {
  /** Base64 puro do JPEG, sem o prefix {@code data:image/jpeg;base64,}. */
  imageBase64: string;
  format: 'jpeg';
  width: number;
  height: number;
  /** Diagnostics da captura — útil pra debugar snapshots pretos em TV. */
  diagnostics?: CaptureDiagnostics;
}

export interface VideoDiagnostic {
  readyState: number;
  videoWidth: number;
  videoHeight: number;
  paused: boolean;
  rect: { x: number; y: number; w: number; h: number };
  srcType:
    | 'mediastream'
    | 'blob'
    | 'http'
    | 'https'
    | 'file'
    | 'data'
    | 'empty'
    | 'other';
  crossOrigin: string | null;
  capturable: boolean;
  drawResult:
    | 'drawn'
    | 'logical-snapshot-fallback'
    | 'placeholder-not-capturable'
    | 'placeholder-error'
    | 'placeholder-hw-overlay'
    | 'skipped-zero-size';
  drawError?: string;
  /** URL do frame remoto tentado quando drawResult === 'logical-snapshot-fallback'. */
  frameUrl?: string;
  /** true quando a mini veio do cache local (mesmo contentId). */
  frameFromCache?: boolean;
  /** Ratio de pixels pretos amostrados pós-drawImage (0..1). undefined se sampling falhou. */
  blackRatio?: number;
  /** Qual caminho de captura foi usado pra esse vídeo. */
  captureMethod?: 'ImageCapture' | 'WebGL' | 'rVFC' | 'drawImage';
  /** Por que rVFC não foi usado (quando captureMethod === 'drawImage' E o
   *  WebGL também não tentou ou falhou). */
  rVFCSkipReason?:
    | 'unsupported'
    | 'timeout'
    | 'create-bitmap-error'
    | 'no-attempt';
  /** Por que WebGL não foi usado (quando captureMethod !== 'WebGL'). */
  webGLSkipReason?:
    | 'unsupported'
    | 'init-failed'
    | 'tex-image-failed'
    | 'zero-size';
  /** Por que ImageCapture não foi usado (quando captureMethod !== 'ImageCapture'). */
  imageCaptureSkipReason?:
    | 'unsupported'
    | 'no-tracks'
    | 'grab-failed'
    | 'capturestream-failed';
}

export interface CaptureDiagnostics {
  targetId: string;
  targetTag: string;
  targetRect: { w: number; h: number };
  renderScale: number;
  domCanvasOk: boolean;
  domCanvasDims: { w: number; h: number } | null;
  videoCount: number;
  videos: VideoDiagnostic[];
  durationMs: number;
  /** Preenchido quando algum vídeo usou snapshot lógico (fallback). */
  playbackContext?: PlaybackContext;
}

/**
 * Captura a tela e retorna o canvas composto (DOM + vídeos).
 *
 * <p>Função compartilhada por {@link captureScreenBase64} e
 * {@code ScreenshotService.capture}. Retorna o canvas pronto para conversão
 * em base64 (data URL) ou Blob.</p>
 *
 * @returns canvas final composto, ou {@code null} se a captura DOM falhar
 */
/**
 * Mutex global de captura. Sem isso, dois caminhos disparam screenshot
 * simultâneo (SSE command em ContentSSEService:303 + RemoteOps WS em
 * RemoteOpsService:312) e compartilham o WebGL context cacheado em
 * webglCtx (linha ~531) — race condition causa crash do app inteiro
 * em Tizen (memory pressure + GPU state inconsistente). Bug Renato
 * 2026-05-28: TV saiu do app pra home ao clicar "Capturar screenshot"
 * no painel admin (que dispara os 2 caminhos).
 */
let captureMutex: Promise<unknown> | null = null;

export async function captureScreenToCanvas(): Promise<{
  canvas: HTMLCanvasElement | null;
  diagnostics: CaptureDiagnostics;
}> {
  // Se já tem captura em andamento, AGUARDA ela completar antes de iniciar
  // a próxima. Evita context WebGL compartilhado em concorrência E também
  // multiplicação de getImageData (readbacks pesados) em paralelo.
  if (captureMutex) {
    try {
      await captureMutex;
    } catch {
      /* swallow — a tentativa anterior já registrou seu próprio erro */
    }
  }

  let resolveMutex: () => void = () => {};
  captureMutex = new Promise<void>((resolve) => {
    resolveMutex = resolve;
  });

  try {
    return await withCaptureTimeout(captureScreenToCanvasInternal());
  } finally {
    resolveMutex();
    captureMutex = null;
  }
}

async function captureScreenToCanvasInternal(): Promise<{
  canvas: HTMLCanvasElement | null;
  diagnostics: CaptureDiagnostics;
}> {
  const t0 = Date.now();
  probeCapturePlatformCapabilities();

  // SSSP / browser com getDisplayMedia: captura tela composta (vídeo + overlays)
  // num único passo — evita readback por <video> no HW overlay Tizen.
  if (shouldTryDisplayMediaCapture()) {
    const dmCanvas = await tryCaptureViaDisplayMedia(MAX_WIDTH, MAX_HEIGHT);
    if (dmCanvas) {
      return {
        canvas: dmCanvas,
        diagnostics: {
          targetId: 'display-media',
          targetTag: 'screen',
          targetRect: { w: dmCanvas.width, h: dmCanvas.height },
          renderScale: 1,
          domCanvasOk: true,
          domCanvasDims: { w: dmCanvas.width, h: dmCanvas.height },
          videoCount: 0,
          videos: [],
          durationMs: Date.now() - t0,
        },
      };
    }
  }

  const target =
    document.getElementById('stream-container') ||
    document.getElementById('app') ||
    document.getElementById('root') ||
    document.body;

  const targetRectInit = target.getBoundingClientRect();
  const diagnostics: CaptureDiagnostics = {
    targetId: target.id || '(none)',
    targetTag: target.tagName.toLowerCase(),
    targetRect: { w: targetRectInit.width, h: targetRectInit.height },
    renderScale: 1,
    domCanvasOk: false,
    domCanvasDims: null,
    videoCount: 0,
    videos: [],
    durationMs: 0,
  };

  try {
    const layoutW = Math.max(1, target.offsetWidth || targetRectInit.width);
    const layoutH = Math.max(1, target.offsetHeight || targetRectInit.height);
    const scaleX = MAX_WIDTH / layoutW;
    const scaleY = MAX_HEIGHT / layoutH;
    const renderScale = Math.min(1, scaleX, scaleY); // Nunca scale up
    diagnostics.renderScale = renderScale;
    diagnostics.targetRect = { w: layoutW, h: layoutH };

    // Tizen: pula html2canvas — DOM traz textos/UI no meio; snapshot lógico
    // compõe canvas limpo (preto + mini centralizada, legenda só no admin).
    const tizenLogicalOnly = await tryTizenLogicalSnapshotOnly(
      target,
      renderScale,
      t0,
      diagnostics,
    );
    if (tizenLogicalOnly) {
      return tizenLogicalOnly;
    }

    const scaledDirect = await tryCaptureScaledViewportDirect(
      target,
      renderScale,
      t0,
      diagnostics,
    );
    if (scaledDirect) {
      return scaledDirect;
    }

    // 1) Captura o DOM SEM os <video> — vamos compor manualmente depois
    //    com drawImage, que faz readback do GPU overlay (resolve preto em
    //    Tizen/webOS modernos).
    const domCanvas = await html2canvas(target, {
      useCORS: true,
      allowTaint: false,
      scale: renderScale,
      logging: false,
      ignoreElements: (el) => el.tagName === 'VIDEO',
      onclone: (clonedDoc) => {
        neutralizeScaledViewportInClone(clonedDoc);
      },
    });

    diagnostics.domCanvasOk = true;
    diagnostics.domCanvasDims = { w: domCanvas.width, h: domCanvas.height };

    // EIXO 1 (2026-05-29): em Tizen pulamos toda a captura de vídeo (HW
    // overlay → readback nativo mata o app). SSSP tenta getDisplayMedia antes.
    const tizenSkipVideo = shouldSkipTizenVideoReadback();

    // 2) Compor cada <video> visível via drawImage com object-fit: cover.
    // willReadFrequently: true — sampleBlackRatio chama getImageData N
    // vezes por vídeo (linha ~377). Sem essa flag, o browser otimiza o
    // canvas pra GPU-only e cada readback dispara um copy GPU→CPU. Em
    // Tizen com pouca RAM, multiplicado por vários vídeos = OOM + crash.
    // Em Tizen NÃO pedimos willReadFrequently: não há getImageData no
    // caminho (só desenhamos placeholder), então o flag só atrapalharia.
    const ctx = tizenSkipVideo
      ? domCanvas.getContext('2d')
      : domCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      console.warn('[captureScreen] domCanvas getContext("2d") retornou null');
      diagnostics.durationMs = Date.now() - t0;
      return { canvas: domCanvas, diagnostics }; // Retorna canvas com DOM, sem vídeo
    }

    // Só o que está NA TELA: o standby do motor A/B fica no DOM pausado e
    // invisível, e desenhá-lo cobria o frame real com "⏸ Vídeo pausado".
    const videos = Array.from(
      target.querySelectorAll<HTMLVideoElement>('video'),
    ).filter(isVideoOnScreen);
    diagnostics.videoCount = videos.length;

    for (const video of videos) {
      const { destX, destY, destW, destH } = videoDestInCaptureCanvas(
        target,
        video,
        renderScale,
      );

      const vdiag: VideoDiagnostic = {
        readyState: video.readyState,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        paused: video.paused,
        rect: { x: destX, y: destY, w: destW, h: destH },
        srcType: classifyVideoSrc(video),
        crossOrigin: video.crossOrigin,
        capturable: isVideoCapturable(video),
        drawResult: 'skipped-zero-size',
      };

      // Pula vídeos invisíveis ou fora do viewport do target
      if (destW <= 0 || destH <= 0) {
        diagnostics.videos.push(vdiag);
        continue;
      }

      if (tizenSkipVideo) {
        // EIXO 1: Tizen HW overlay — NÃO tentamos readback do frame ao vivo
        // (ImageCapture/WebGL/rVFC/drawImage matam o processo). Depois de
        // esgotar essa via, tentamos thumbnail estático do servidor.
        await applyVideoCaptureFallback(
          ctx,
          destX,
          destY,
          destW,
          destH,
          video,
          vdiag,
          diagnostics,
          {
            placeholderKind: 'placeholder-hw-overlay',
          },
        );
      } else if (vdiag.capturable) {
        try {
          const drawInfo = await drawVideoWithObjectFitCover(
            ctx,
            video,
            destX,
            destY,
            destW,
            destH,
          );
          vdiag.drawResult = 'drawn';
          vdiag.captureMethod = drawInfo.method;
          if (drawInfo.rVFCSkipReason) {
            vdiag.rVFCSkipReason = drawInfo.rVFCSkipReason;
          }
          if (drawInfo.webGLSkipReason) {
            vdiag.webGLSkipReason = drawInfo.webGLSkipReason;
          }
          if (drawInfo.imageCaptureSkipReason) {
            vdiag.imageCaptureSkipReason = drawInfo.imageCaptureSkipReason;
          }

          // Detecção de "silent GPU readback failure": Tizen/webOS Consumer
          // colocam o <video> num HW overlay plane separado do compositor
          // HTML. ctx.drawImage(video) NÃO lança exception, mas o pixel
          // readback do canvas pega o plane vazio (preto puro). O caminho
          // rVFC + ImageBitmap normalmente resolve isso (frame CPU-side).
          // Mantemos sampling como rede de segurança: se mesmo após rVFC
          // o frame veio preto, repinta com placeholder informativo.
          const blackRatio = sampleBlackRatio(ctx, destX, destY, destW, destH);
          vdiag.blackRatio = blackRatio;
          if (blackRatio !== undefined && blackRatio >= 0.98) {
            console.warn(
              `[captureScreen] video frame silenciosamente preto (${Math.round(
                blackRatio * 100,
              )}% pretos) via ${drawInfo.method} — HW overlay; tentando snapshot lógico`,
            );
            await applyVideoCaptureFallback(
              ctx,
              destX,
              destY,
              destW,
              destH,
              video,
              vdiag,
              diagnostics,
              {
                placeholderKind: 'placeholder-hw-overlay',
              },
            );
          }
        } catch (err) {
          vdiag.drawError =
            err instanceof Error ? `${err.name}: ${err.message}` : String(err);
          console.warn(
            '[captureScreen] drawImage(video) falhou — tentando snapshot lógico:',
            err,
          );
          await applyVideoCaptureFallback(
            ctx,
            destX,
            destY,
            destW,
            destH,
            video,
            vdiag,
            diagnostics,
            {
              placeholderKind: 'placeholder-error',
            },
          );
        }
      } else {
        await applyVideoCaptureFallback(
          ctx,
          destX,
          destY,
          destW,
          destH,
          video,
          vdiag,
          diagnostics,
          {
            placeholderKind: 'placeholder-not-capturable',
          },
        );
      }

      diagnostics.videos.push(vdiag);
    }

    diagnostics.durationMs = Date.now() - t0;
    return { canvas: domCanvas, diagnostics };
  } catch (err) {
    console.error('[captureScreen] captureScreenToCanvas falhou:', err);
    diagnostics.durationMs = Date.now() - t0;
    return { canvas: null, diagnostics };
  }
}

/**
 * Android/Capacitor com {@code ScaledViewport}: html2canvas deixa o conteúdo
 * num canto (~1/9 da área quando renderScale≈0.375). Compõe canvas direto
 * em layout × renderScale — vídeo/imagem preenchem o frame inteiro.
 */
async function tryCaptureScaledViewportDirect(
  target: HTMLElement,
  renderScale: number,
  t0: number,
  baseDiagnostics: CaptureDiagnostics,
): Promise<{
  canvas: HTMLCanvasElement;
  diagnostics: CaptureDiagnostics;
} | null> {
  if (!findScaledViewportContent(target)) return null;
  if (shouldSkipTizenVideoReadback()) return null;

  const layoutW = Math.max(1, target.offsetWidth);
  const layoutH = Math.max(1, target.offsetHeight);
  const w = Math.max(1, Math.round(layoutW * renderScale));
  const h = Math.max(1, Math.round(layoutH * renderScale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, w, h);

  // Idem: desenhar o standby (pausado/invisível) por cima do ativo era o que fazia
  // a captura sair "⏸ Vídeo pausado" com o vídeo tocando normalmente.
  const videos = Array.from(
    target.querySelectorAll<HTMLVideoElement>('video'),
  ).filter(isVideoOnScreen);
  const videoDiagnostics: VideoDiagnostic[] = [];

  for (const video of videos) {
    const { destX, destY, destW, destH } = layoutDestInCaptureCanvas(
      target,
      video,
      renderScale,
    );

    const vdiag: VideoDiagnostic = {
      readyState: video.readyState,
      videoWidth: video.videoWidth,
      videoHeight: video.videoHeight,
      paused: video.paused,
      rect: { x: destX, y: destY, w: destW, h: destH },
      srcType: classifyVideoSrc(video),
      crossOrigin: video.crossOrigin,
      capturable: isVideoCapturable(video),
      drawResult: 'skipped-zero-size',
    };

    if (destW <= 0 || destH <= 0) {
      videoDiagnostics.push(vdiag);
      continue;
    }

    if (vdiag.capturable) {
      try {
        const drawInfo = await drawVideoWithObjectFitCover(
          ctx,
          video,
          destX,
          destY,
          destW,
          destH,
        );
        vdiag.drawResult = 'drawn';
        vdiag.captureMethod = drawInfo.method;
        if (drawInfo.rVFCSkipReason)
          vdiag.rVFCSkipReason = drawInfo.rVFCSkipReason;
        if (drawInfo.webGLSkipReason)
          vdiag.webGLSkipReason = drawInfo.webGLSkipReason;
        if (drawInfo.imageCaptureSkipReason) {
          vdiag.imageCaptureSkipReason = drawInfo.imageCaptureSkipReason;
        }
      } catch (err) {
        console.warn('[captureScreen] scaled-viewport drawVideo falhou:', err);
        await applyVideoCaptureFallback(
          ctx,
          destX,
          destY,
          destW,
          destH,
          video,
          vdiag,
          baseDiagnostics,
          { placeholderKind: 'placeholder-error' },
        );
      }
    } else {
      await applyVideoCaptureFallback(
        ctx,
        destX,
        destY,
        destW,
        destH,
        video,
        vdiag,
        baseDiagnostics,
        { placeholderKind: 'placeholder-not-capturable' },
      );
    }

    videoDiagnostics.push(vdiag);
  }

  const images = Array.from(
    target.querySelectorAll<HTMLImageElement>('img'),
  ).filter((img) => img.offsetWidth > 0 && img.offsetHeight > 0);

  for (const img of images) {
    const { destX, destY, destW, destH } = layoutDestInCaptureCanvas(
      target,
      img,
      renderScale,
    );
    if (destW <= 0 || destH <= 0) continue;
    try {
      ctx.drawImage(img, destX, destY, destW, destH);
    } catch (err) {
      console.warn(
        '[captureScreen] scaled-viewport drawImage(img) falhou:',
        err,
      );
    }
  }

  return {
    canvas,
    diagnostics: {
      ...baseDiagnostics,
      domCanvasOk: true,
      domCanvasDims: { w, h },
      videoCount: videos.length,
      videos: videoDiagnostics,
      durationMs: Date.now() - t0,
    },
  };
}

/**
 * Tizen HW overlay: canvas só com preto + mini centralizada (sem html2canvas).
 * Evita textos duplicados do DOM e legenda queimada no JPEG.
 */
async function tryTizenLogicalSnapshotOnly(
  target: HTMLElement,
  renderScale: number,
  t0: number,
  baseDiagnostics: CaptureDiagnostics,
): Promise<{
  canvas: HTMLCanvasElement;
  diagnostics: CaptureDiagnostics;
} | null> {
  if (!shouldSkipTizenVideoReadback()) return null;

  const targetRect = target.getBoundingClientRect();
  const videos = Array.from(
    target.querySelectorAll<HTMLVideoElement>('video'),
  ).filter((v) => isVideoOnScreen(v) && resolveVideoContentId(v));

  const video = videos[0];
  if (!video) return null;

  const w = Math.max(1, Math.round(targetRect.width * renderScale));
  const h = Math.max(1, Math.round(targetRect.height * renderScale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const logical = await composeLogicalSnapshotFullFrame(ctx, video);
  if (!logical.ok) return null;

  const rect = video.getBoundingClientRect();
  const vdiag: VideoDiagnostic = {
    readyState: video.readyState,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    paused: video.paused,
    rect: {
      x: (rect.left - targetRect.left) * renderScale,
      y: (rect.top - targetRect.top) * renderScale,
      w: rect.width * renderScale,
      h: rect.height * renderScale,
    },
    srcType: classifyVideoSrc(video),
    crossOrigin: video.crossOrigin,
    capturable: isVideoCapturable(video),
    drawResult: 'logical-snapshot-fallback',
    frameUrl: logical.frameUrl,
    frameFromCache: logical.fromCache,
  };

  return {
    canvas,
    diagnostics: {
      ...baseDiagnostics,
      domCanvasOk: false,
      domCanvasDims: { w, h },
      videoCount: videos.length,
      videos: [vdiag],
      playbackContext: logical.playbackContext,
      durationMs: Date.now() - t0,
    },
  };
}

/**
 * Classifica o src do vídeo num bucket de alto nível pra telemetria.
 * Útil pra entender em campo se o vídeo veio de cache (blob:/file:) ou
 * remote (https:), o que muda o comportamento de readback em alguns engines.
 */
function classifyVideoSrc(video: HTMLVideoElement): VideoDiagnostic['srcType'] {
  // MediaStream tem srcObject; src costuma ser empty
  if (video.srcObject) return 'mediastream';
  const src = video.currentSrc || video.src || '';
  if (!src) return 'empty';
  if (src.startsWith('blob:')) return 'blob';
  if (src.startsWith('file:')) return 'file';
  if (src.startsWith('data:')) return 'data';
  if (src.startsWith('https:')) return 'https';
  if (src.startsWith('http:')) return 'http';
  return 'other';
}

/**
 * Captura a tela e retorna base64.
 * Retorna {@code null} se a captura falhar (canvas tainted, OOM, etc.) —
 * o caller deve tratar como "snapshot indisponível".
 */
export async function captureScreenBase64(): Promise<ScreenCapture | null> {
  const { canvas, diagnostics } = await captureScreenToCanvas();
  if (!canvas) return null;

  try {
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    // dataUrl = "data:image/jpeg;base64,<payload>"
    const commaIdx = dataUrl.indexOf(',');
    if (commaIdx < 0) {
      console.warn('[captureScreen] dataUrl sem comma — captura falhou');
      return null;
    }
    const base64 = dataUrl.slice(commaIdx + 1);

    return {
      imageBase64: base64,
      format: 'jpeg',
      width: canvas.width,
      height: canvas.height,
      diagnostics,
    };
  } catch (err) {
    console.error('[captureScreen] toDataURL falhou:', err);
    return null;
  }
}

/**
 * Vídeo é capturável quando tem frame decodificado disponível
 * ({@code readyState >= 2} = HAVE_CURRENT_DATA) e dimensões intrínsecas válidas.
 */
function isVideoCapturable(video: HTMLVideoElement): boolean {
  return video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
}

/** Threshold de luminância pra considerar pixel "preto puro" (Y do YUV). */
const BLACK_LUMA_THRESHOLD = 4;
/** Número máximo de pixels amostrados por vídeo (perf cap pra TVs antigas). */
const MAX_SAMPLES = 200;

/**
 * Amostra pixels do retângulo {@code (x,y,w,h)} no canvas e retorna a fração
 * de pixels considerados "pretos puros". Usado pra detectar falhas silenciosas
 * de {@code drawImage(video)} em HW overlays (Tizen/webOS Consumer TVs).
 *
 * <p>Retorna {@code undefined} se {@code getImageData} lançar (canvas tainted).
 * Nesse caso o caller deve assumir "draw OK" e continuar — não dá pra
 * diferenciar falha silenciosa de canvas tainted.</p>
 *
 * <p>Sampling em grid quase-uniforme (sqrt(MAX_SAMPLES) cells), pra evitar
 * viés de borda em vídeos com letterbox.</p>
 */
function sampleBlackRatio(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
): number | undefined {
  if (w < 4 || h < 4) return undefined;

  try {
    // Clamp ao canvas (pra Tizen, drawImage pode estender o destino além)
    const canvas = ctx.canvas;
    const canvasW = canvas?.width;
    const canvasH = canvas?.height;
    // Em test envs com mock de canvas, .width pode ser undefined
    if (typeof canvasW !== 'number' || typeof canvasH !== 'number') {
      return undefined;
    }
    const sx = Math.max(0, Math.floor(x));
    const sy = Math.max(0, Math.floor(y));
    const sw = Math.min(canvasW - sx, Math.floor(w));
    const sh = Math.min(canvasH - sy, Math.floor(h));
    if (sw < 4 || sh < 4) return undefined;

    if (typeof ctx.getImageData !== 'function') return undefined;
    const data = ctx.getImageData(sx, sy, sw, sh).data;
    const totalPixels = (sw * sh) | 0;
    const step = Math.max(1, Math.floor(totalPixels / MAX_SAMPLES));
    let samples = 0;
    let blacks = 0;
    for (let i = 0; i < totalPixels; i += step) {
      const offset = i * 4;
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      // Luminância aproximada (Rec. 709)
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      if (luma <= BLACK_LUMA_THRESHOLD) blacks++;
      samples++;
    }
    return samples > 0 ? blacks / samples : undefined;
  } catch {
    // Canvas tainted ou OOM — assume draw OK e segue.
    return undefined;
  }
}

/**
 * Computa cropping {@code object-fit: cover} pra dimensões fonte (src) dadas
 * o tamanho de destino. Centralizado em função pra ser compartilhado entre
 * o caminho rVFC (ImageBitmap) e o fallback drawImage(video).
 */
function computeObjectFitCover(
  srcWidth: number,
  srcHeight: number,
  destW: number,
  destH: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const srcRatio = srcWidth / srcHeight;
  const destRatio = destW / destH;
  let sx = 0;
  let sy = 0;
  let sw = srcWidth;
  let sh = srcHeight;
  if (srcRatio > destRatio) {
    // Fonte mais larga que destino → cropa laterais (mantém altura)
    sw = srcHeight * destRatio;
    sx = (srcWidth - sw) / 2;
  } else if (srcRatio < destRatio) {
    // Fonte mais alta que destino → cropa topo/base (mantém largura)
    sh = srcWidth / destRatio;
    sy = (srcHeight - sh) / 2;
  }
  return { sx, sy, sw, sh };
}

const RVFC_TIMEOUT_MS = 2500;

/**
 * Captura frame via {@code HTMLVideoElement.captureStream()} +
 * {@code ImageCapture.grabFrame()}.
 *
 * <p><b>Por que isso pode funcionar onde drawImage/rVFC/WebGL falham
 * em Tizen Consumer 9.0:</b></p>
 *
 * <p>Os outros caminhos leem o frame do COMPOSITOR (Canvas 2D drawImage),
 * do FRAMEBUFFER (WebGL texImage2D), ou da CALLBACK DE DECODER (rVFC) — todos
 * blindados pelo HW overlay plane do Tizen.</p>
 *
 * <p>{@code video.captureStream()} retorna um {@code MediaStream} que vem
 * direto do DECODER PIPELINE (antes do HW overlay). {@code ImageCapture}
 * então pode invocar {@code grabFrame()} naquele track, retornando um
 * ImageBitmap CPU-side. Esse caminho é completamente separado do compositor
 * gráfico — em alguns engines (incluindo certos builds de Chromium em
 * Tizen) ele funciona enquanto os outros falham.</p>
 *
 * <p>Disponibilidade:</p>
 * <ul>
 *   <li>{@code captureStream()}: Chrome ≥51, mas em Tizen Consumer 9.0
 *       a feature pode estar mockada (retorna stream com 0 tracks).</li>
 *   <li>{@code ImageCapture}: Chrome ≥59 desktop. Em mobile Chrome ≥83.
 *       Em Tizen 9.0 (Chromium 108) deveria estar disponível.</li>
 * </ul>
 *
 * @returns ImageBitmap (caller deve chamar {@code .close()} após uso) ou
 *   motivo de skip pra telemetria
 */
async function captureVideoFrameViaImageCapture(
  video: HTMLVideoElement,
): Promise<
  | { bitmap: ImageBitmap }
  | {
      bitmap: null;
      reason:
        | 'unsupported'
        | 'no-tracks'
        | 'grab-failed'
        | 'capturestream-failed';
    }
> {
  const videoAny = video as HTMLVideoElement & {
    captureStream?: () => MediaStream;
  };

  if (typeof videoAny.captureStream !== 'function') {
    return { bitmap: null, reason: 'unsupported' };
  }
  const ImageCaptureCtor = (
    window as unknown as {
      ImageCapture?: new (track: MediaStreamTrack) => {
        grabFrame: () => Promise<ImageBitmap>;
      };
    }
  ).ImageCapture;

  if (typeof ImageCaptureCtor !== 'function') {
    return { bitmap: null, reason: 'unsupported' };
  }

  let stream: MediaStream;
  try {
    stream = videoAny.captureStream();
  } catch {
    return { bitmap: null, reason: 'capturestream-failed' };
  }

  const tracks = stream.getVideoTracks();
  if (tracks.length === 0) {
    return { bitmap: null, reason: 'no-tracks' };
  }

  try {
    const imageCapture = new ImageCaptureCtor(tracks[0]);
    const bitmap = await imageCapture.grabFrame();
    return { bitmap };
  } catch {
    return { bitmap: null, reason: 'grab-failed' };
  } finally {
    // Limpa tracks. captureStream() cria nova stream a cada call — sem stop
    // o decoder fica reportando MediaStream extra na pipeline.
    tracks.forEach((t) => t.stop());
  }
}

/**
 * Cache do contexto WebGL pra evitar reinicialização (gl.createProgram, shaders
 * compile) a cada captura. Reaproveitado entre chamadas. {@code null} quando
 * ainda não foi inicializado ou falhou em criar.
 */
interface WebGLCaptureContext {
  canvas: HTMLCanvasElement;
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  positionLoc: number;
  texCoordLoc: number;
  texture: WebGLTexture;
  positionBuf: WebGLBuffer;
  texCoordBuf: WebGLBuffer;
}
let webglCtx: WebGLCaptureContext | null = null;
let webglInitFailed = false;

function getWebGLCaptureContext(): WebGLCaptureContext | null {
  // EIXO 1: em Tizen nunca criamos/cacheamos contexto WebGL. O readback de
  // vídeo via texImage2D volta preto (HW overlay) e ainda pressiona a GPU/RAM
  // da TV. Nenhum caminho de captura de vídeo roda em Tizen — esse guard é
  // defesa em profundidade pra garantir que o contexto não seja alocado.
  if (isTizenPlatform()) return null;
  if (webglCtx) return webglCtx;
  if (webglInitFailed) return null;

  try {
    const canvas = document.createElement('canvas');
    // FIX gargalo GPU (Renato 2026-07-05): preserveDrawingBuffer=false. Ele
    // forçava o driver a manter o backing store entre frames → dobrava a pegada
    // de memória gráfica. O caller consome o frame via drawImage(webglCanvas)
    // SÍNCRONO logo após o drawArrays (mesma execução), então não é preciso
    // preservar o buffer entre compositions. Reduz a pressão de EGL no A7.
    const glOpts = {
      preserveDrawingBuffer: false,
      premultipliedAlpha: false,
    };
    const gl =
      (canvas.getContext('webgl', glOpts) as WebGLRenderingContext | null) ||
      (canvas.getContext(
        'experimental-webgl',
        glOpts,
      ) as WebGLRenderingContext | null);

    if (!gl) {
      webglInitFailed = true;
      return null;
    }

    // Shaders mínimos: quad fullscreen com textura. flipY=true compensa o Y
    // invertido do WebGL (origem bottom-left vs canvas 2D top-left).
    const vsSrc = `
      attribute vec2 aPos;
      attribute vec2 aTex;
      varying vec2 vTex;
      void main() {
        vTex = aTex;
        gl_Position = vec4(aPos, 0.0, 1.0);
      }
    `;
    const fsSrc = `
      precision mediump float;
      varying vec2 vTex;
      uniform sampler2D uTex;
      void main() {
        gl_FragColor = texture2D(uTex, vTex);
      }
    `;

    const compile = (type: number, src: string): WebGLShader => {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const log = gl.getShaderInfoLog(shader);
        gl.deleteShader(shader);
        throw new Error(`Shader compile failed: ${log}`);
      }
      return shader;
    };

    const vs = compile(gl.VERTEX_SHADER, vsSrc);
    const fs = compile(gl.FRAGMENT_SHADER, fsSrc);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`Program link failed: ${gl.getProgramInfoLog(program)}`);
    }

    const positionLoc = gl.getAttribLocation(program, 'aPos');
    const texCoordLoc = gl.getAttribLocation(program, 'aTex');

    // Quad fullscreen no clip space (-1..1)
    const positionBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    // Textura coords (0..1) com Y flippado pra alinhar com canvas 2D
    const texCoordBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]),
      gl.STATIC_DRAW,
    );

    const texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    webglCtx = {
      canvas,
      gl,
      program,
      positionLoc,
      texCoordLoc,
      texture,
      positionBuf,
      texCoordBuf,
    };
    return webglCtx;
  } catch (err) {
    console.warn('[captureScreen] WebGL init failed:', err);
    webglInitFailed = true;
    return null;
  }
}

/**
 * Captura o frame atual do vídeo via WebGL {@code texImage2D(GL_TEXTURE_2D, ...,
 * video)}, retornando o canvas WebGL com o frame renderizado.
 *
 * <p><b>Por que isso resolve o "frame preto" em Tizen Consumer 9.0:</b></p>
 *
 * <p>Em Tizen/webOS modernos, o {@code <video>} é renderizado num HW overlay
 * plane separado do compositor HTML. {@code drawImage(video)} no Canvas 2D
 * lê do framebuffer do compositor — que tem o plano do vídeo VAZIO (preto).
 * O caminho rVFC + createImageBitmap deveria contornar isso mas a API
 * existe-só-no-nome no WebView do Tizen Consumer 9.0 (callback nunca dispara).</p>
 *
 * <p>{@code gl.texImage2D(target, level, format, format, type, video)} força o
 * browser a fazer uma cópia explícita do plano do vídeo pra uma textura GPU
 * acessível. Diferente do Canvas 2D path que lê do compositor, o WebGL path
 * passa por uma API que o Chromium implementa em código separado e que sempre
 * funciona pra ler vídeo, mesmo em HW overlays.</p>
 *
 * <p>Referência: a equipe do Chromium documenta esse comportamento em
 * https://crbug.com/873907 — Canvas 2D usa fast-path compositor, WebGL usa
 * readback explícito que sempre traz pixels reais.</p>
 *
 * @returns canvas WebGL com frame renderizado (caller pode chamar
 *   {@code drawImage(canvas, ...)} no canvas 2D destino), ou {@code null} se
 *   o caminho WebGL não tá disponível ou falhou
 */
function captureVideoFrameViaWebGL(
  video: HTMLVideoElement,
): HTMLCanvasElement | null {
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return null;
  }

  const ctx = getWebGLCaptureContext();
  if (!ctx) return null;

  const {
    canvas,
    gl,
    program,
    positionLoc,
    texCoordLoc,
    texture,
    positionBuf,
    texCoordBuf,
  } = ctx;

  try {
    // Resize canvas pra match video (preserva nativa do vídeo, sem scaling
    // perdoso de informação aqui — o caller faz o object-fit cover).
    // FIX gargalo GPU (Renato 2026-07-05): SÓ re-aloca o drawing buffer WebGL se
    // a resolução MUDOU. Antes, setar canvas.width/height a cada captura
    // re-alocava ~8MB de backing store GPU TODA vez (piso 1s no Android) →
    // fragmentava o pool de memória gráfica EGL → MediaCodec.configure falhava
    // (error -12 / sendCapacityError → 'Cannot configure video codec') → travava
    // a reprodução. Como os vídeos são todos 1080p, o buffer fica estável e não
    // churna. Confirmado no A7: sem o screenshot, zero erro de codec.
    if (
      canvas.width !== video.videoWidth ||
      canvas.height !== video.videoHeight
    ) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    gl.viewport(0, 0, canvas.width, canvas.height);

    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(program);

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuf);
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuf);
    gl.enableVertexAttribArray(texCoordLoc);
    gl.vertexAttribPointer(texCoordLoc, 2, gl.FLOAT, false, 0, 0);

    gl.bindTexture(gl.TEXTURE_2D, texture);
    // EMPACOTAMENTO: UNPACK_FLIP_Y_WEBGL=true seria outra forma de lidar com
    // Y invertido. Optamos por inverter no shader (texCoordBuf) porque é
    // mais consistente em Chromium quando o source é HTMLVideoElement.
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    // CRÍTICO: esta linha é onde o Chromium faz o readback do vídeo da GPU
    // pra textura. Em Tizen com HW overlay, este caminho FUNCIONA enquanto
    // o Canvas 2D drawImage(video) retorna preto.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    return canvas;
  } catch (err) {
    console.warn('[captureScreen] WebGL texImage2D failed:', err);
    return null;
  }
}

/**
 * Captura o frame atual do vídeo e desenha num canvas alvo (object-fit: cover).
 *
 * <p>Helper público usado pelo "Canvas Bridge" no Stream — exibir o último
 * frame do vídeo congelado durante a transição pra próxima mídia. Visualmente
 * idêntico a "vídeo travado no último frame" mas usa apenas 1 video element
 * no DOM (não reproduz o crash do Tizen Consumer 9.0 que ocorreu em
 * v1.0.30/31/32 com VideoPreloader paralelo).</p>
 *
 * <p>Tenta dois caminhos em ordem (mesmo padrão de
 * {@link drawVideoWithObjectFitCover}, que é private):</p>
 * <ol>
 *   <li><b>rVFC + createImageBitmap</b> — funciona em Tizen 9.0+ Consumer onde
 *       o HW overlay bloqueia o readback do canvas via drawImage direto.</li>
 *   <li><b>drawImage(video) direto</b> — fallback pra Tizen 7.x, webOS antigo
 *       e navegadores sem rVFC. Em Tizen Consumer 9.0 retorna frame preto
 *       (HW overlay), mas é melhor do que nada.</li>
 * </ol>
 *
 * <p>O canvas alvo deve ter dimensões pré-setadas pelo caller (geralmente
 * iguais ao retângulo do {@code <video>} no DOM).</p>
 *
 * @param video Vídeo a capturar (assume {@code crossOrigin="anonymous"})
 * @param canvas Canvas alvo onde desenhar — caller controla dimensões
 * @returns true se o frame foi desenhado, false se rVFC E drawImage falharam
 *   (ex: DRM, vídeo sem dimensões, decode error)
 */
/**
 * Limite de pretitude pra considerar o frame válido pro Canvas Bridge.
 * Acima disso, o canvas seria mostrado como tela preta sólida durante
 * a transição (pior do que o overlay de loading). Em Tizen Consumer 9.0,
 * todos os caminhos (drawImage, rVFC, WebGL) retornam ~0.99 preto quando
 * o vídeo está em HW overlay → drawVideoFrameToCanvas detecta e retorna
 * false, fazendo o Stream cair no comportamento legacy (mostrar overlay).
 */
const CANVAS_BRIDGE_BLACK_THRESHOLD = 0.5;

/**
 * Verifica se o canvas destino contém um frame visualmente útil (não-preto).
 * Em Tizen Consumer 9.0, o canvas pode receber pixels mas todos pretos por
 * causa do HW overlay. Caller deve usar isso pra decidir mostrar ou esconder.
 *
 * <p><b>Performance crítica:</b> esta função é chamada a CADA transição de
 * vídeo (a cada ~10s no loop de playlist). Em vez de {@code sampleBlackRatio}
 * (que faz {@code getImageData} do canvas inteiro — 3.6 MB GPU→CPU em 1280×720),
 * lê apenas 16 pixels em pontos estratégicos (~64 bytes total).</p>
 *
 * <p>Tradeoff: pode dar falso negativo se o vídeo tem 16 áreas pretas
 * coincidindo com os 16 pontos amostrados, mas em conteúdo retail real
 * (vídeos com cores vibrantes) é extremamente improvável. Falso positivo
 * (frame preto detectado como OK) é o erro mais perigoso porque mostra
 * canvas preto na tela — mas é matematicamente impossível ter 16 pontos
 * não-pretos num frame 99% preto.</p>
 */
function isCanvasFrameUsable(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): boolean {
  const w = canvas.width;
  const h = canvas.height;
  if (w < 16 || h < 16) return true; // canvas muito pequeno, otimismo
  if (typeof ctx.getImageData !== 'function') return true;

  try {
    // Grade 4x4 — 16 pontos espalhados, longe das bordas (que podem ter
    // letterbox preto legítimo). Margem de 12.5% nas bordas.
    const marginX = Math.floor(w * 0.125);
    const marginY = Math.floor(h * 0.125);
    const usableW = w - marginX * 2;
    const usableH = h - marginY * 2;
    const stepX = Math.max(1, Math.floor(usableW / 3));
    const stepY = Math.max(1, Math.floor(usableH / 3));

    let blacks = 0;
    let samples = 0;
    for (let gy = 0; gy < 4; gy++) {
      for (let gx = 0; gx < 4; gx++) {
        const px = marginX + gx * stepX;
        const py = marginY + gy * stepY;
        // getImageData(x, y, 1, 1) é MUITO mais barato que getImageData
        // de área grande — driver retorna 4 bytes em vez de sw*sh*4.
        const data = ctx.getImageData(px, py, 1, 1).data;
        const luma = 0.2126 * data[0] + 0.7152 * data[1] + 0.0722 * data[2];
        if (luma <= BLACK_LUMA_THRESHOLD) blacks++;
        samples++;
      }
    }
    const ratio = blacks / samples;
    return ratio < CANVAS_BRIDGE_BLACK_THRESHOLD;
  } catch {
    // Canvas tainted ou OOM — otimismo, assume frame OK.
    return true;
  }
}

/**
 * v1.0.70 — Diagnóstico de captura: testa WebCodecs VideoFrame API.
 *
 * Renato (2026-05-28) sugeriu testar WebCodecs antes de partir pra
 * double buffer, na esperança de que essa API mais nova consiga
 * contornar o HW overlay que torna canvas inviável em Tizen Consumer 9.0.
 *
 * WebCodecs `VideoFrame` extrai o frame direto do pipeline do decoder
 * — em teoria, ANTES do compositor onde os outros métodos falham.
 *
 * Esta função NÃO é usada na renderização — apenas mede e retorna
 * status pra diagnóstico no overlay. Se confirmar funcionar, podemos
 * migrar `drawVideoFrameToCanvas` pra usar `VideoFrame` como caminho #0.
 */
export interface VideoCaptureMethodDiag {
  /** Nome do método testado. */
  method: string;
  /** API disponível no runtime? */
  supported: boolean;
  /** Conseguiu extrair pixels (não null/erro)? */
  hasPixels: boolean;
  /** Fração 0..1 de pixels pretos. >0.5 → frame inviável (HW overlay). */
  blackness?: number;
  /** Erro encontrado, se houver. */
  error?: string;
  /** Tempo da operação em ms. */
  durationMs?: number;
}

/**
 * Testa captura via WebCodecs VideoFrame API.
 * Não modifica nada — só extrai e mede.
 */
export async function testWebCodecsVideoFrame(
  video: HTMLVideoElement,
): Promise<VideoCaptureMethodDiag> {
  const start = Date.now();

  // Check API availability
  if (
    typeof (window as unknown as { VideoFrame?: unknown }).VideoFrame ===
    'undefined'
  ) {
    return {
      method: 'WebCodecs VideoFrame',
      supported: false,
      hasPixels: false,
      error: 'VideoFrame API not available',
      durationMs: Date.now() - start,
    };
  }

  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return {
      method: 'WebCodecs VideoFrame',
      supported: true,
      hasPixels: false,
      error: 'video has no dimensions',
      durationMs: Date.now() - start,
    };
  }

  type VideoFrameInstance = {
    displayWidth: number;
    displayHeight: number;
    codedWidth: number;
    codedHeight: number;
    allocationSize(): number;
    copyTo(destination: ArrayBufferView): Promise<unknown>;
    close(): void;
  };
  type VideoFrameCtor = new (source: HTMLVideoElement) => VideoFrameInstance;
  const VideoFrameClass = (window as unknown as { VideoFrame: VideoFrameCtor })
    .VideoFrame;

  let frame: VideoFrameInstance | null = null;
  try {
    frame = new VideoFrameClass(video);
    const w = frame.displayWidth || frame.codedWidth;
    const h = frame.displayHeight || frame.codedHeight;
    if (!w || !h) {
      frame.close();
      return {
        method: 'WebCodecs VideoFrame',
        supported: true,
        hasPixels: false,
        error: 'frame has no dimensions',
        durationMs: Date.now() - start,
      };
    }

    // Allocate buffer pro frame inteiro. copyTo decodifica pixels pra RGBA.
    const size = frame.allocationSize();
    const buffer = new Uint8Array(size);
    await frame.copyTo(buffer);
    frame.close();
    frame = null;

    // Sample 100 random pixels pra calcular blackness.
    // allocationSize/4 = numero de pixels (RGBA).
    const numPixels = size / 4;
    let blackPixels = 0;
    const samples = Math.min(100, numPixels);
    for (let i = 0; i < samples; i++) {
      const pixelIdx = Math.floor(Math.random() * numPixels) * 4;
      const r = buffer[pixelIdx];
      const g = buffer[pixelIdx + 1];
      const b = buffer[pixelIdx + 2];
      const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      if (luma < BLACK_LUMA_THRESHOLD) blackPixels++;
    }
    const blackness = blackPixels / samples;

    return {
      method: 'WebCodecs VideoFrame',
      supported: true,
      hasPixels: true,
      blackness,
      durationMs: Date.now() - start,
    };
  } catch (err) {
    if (frame) {
      try {
        frame.close();
      } catch {
        /* ignore */
      }
    }
    return {
      method: 'WebCodecs VideoFrame',
      supported: true,
      hasPixels: false,
      error: err instanceof Error ? err.message : String(err),
      durationMs: Date.now() - start,
    };
  }
}

/**
 * Testa captura via canvas drawImage tradicional (paralelo de
 * `drawVideoFrameToCanvas` simplificado pra medição).
 */
export async function testCanvasDrawImage(
  video: HTMLVideoElement,
): Promise<VideoCaptureMethodDiag> {
  const start = Date.now();

  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return {
      method: 'Canvas drawImage',
      supported: true,
      hasPixels: false,
      error: 'video has no dimensions',
      durationMs: Date.now() - start,
    };
  }

  try {
    // Canvas temporário 320x180 só pra teste (não vai pra DOM).
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 180;
    // willReadFrequently: true — isCanvasFrameUsable() lê pixels via
    // getImageData(); sem este flag o browser warna "Multiple readback
    // operations using getImageData are faster with willReadFrequently".
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return {
        method: 'Canvas drawImage',
        supported: false,
        hasPixels: false,
        error: '2d context not available',
        durationMs: Date.now() - start,
      };
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Sample blackness no canvas resultante.
    let blackPixels = 0;
    const samples = 16;
    for (let i = 0; i < samples; i++) {
      const x = Math.floor(Math.random() * canvas.width);
      const y = Math.floor(Math.random() * canvas.height);
      const data = ctx.getImageData(x, y, 1, 1).data;
      const luma = 0.2126 * data[0] + 0.7152 * data[1] + 0.0722 * data[2];
      if (luma < BLACK_LUMA_THRESHOLD) blackPixels++;
    }
    const blackness = blackPixels / samples;

    return {
      method: 'Canvas drawImage',
      supported: true,
      hasPixels: true,
      blackness,
      durationMs: Date.now() - start,
    };
  } catch (err) {
    return {
      method: 'Canvas drawImage',
      supported: true,
      hasPixels: false,
      error: err instanceof Error ? err.message : String(err),
      durationMs: Date.now() - start,
    };
  }
}

export async function drawVideoFrameToCanvas(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): Promise<boolean> {
  // willReadFrequently: true — frame analysis via isCanvasFrameUsable usa
  // getImageData repetidamente. Sem flag, Chrome warna no console.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;

  if (video.videoWidth === 0 || video.videoHeight === 0) {
    return false;
  }

  const destW = canvas.width;
  const destH = canvas.height;
  if (destW === 0 || destH === 0) return false;

  // 1) ImageCapture + captureStream — caminho mais robusto contra HW overlay.
  //    Pega o frame direto do decoder pipeline, antes do compositor onde os
  //    outros métodos falham em Tizen Consumer 9.0.
  const imageCapResult = await captureVideoFrameViaImageCapture(video);
  if (imageCapResult.bitmap) {
    try {
      const { sx, sy, sw, sh } = computeObjectFitCover(
        imageCapResult.bitmap.width,
        imageCapResult.bitmap.height,
        destW,
        destH,
      );
      ctx.drawImage(imageCapResult.bitmap, sx, sy, sw, sh, 0, 0, destW, destH);
      imageCapResult.bitmap.close();
      if (isCanvasFrameUsable(ctx, canvas)) return true;
      // Frame preto — cai pros fallbacks (eles podem ter sorte diferente)
    } catch (err) {
      imageCapResult.bitmap.close();
      console.warn(
        '[captureScreen] drawImage(imageCapture bitmap) falhou:',
        err,
      );
      // Cai pros fallbacks
    }
  }

  // 2) WebGL texImage2D — sumimos o frame do HW overlay no Tizen Consumer 9.0
  //    (descobrimos em v1.0.37 que volta preto), mas funciona em Android/browser.
  const webglCanvas = captureVideoFrameViaWebGL(video);
  if (webglCanvas) {
    try {
      const { sx, sy, sw, sh } = computeObjectFitCover(
        webglCanvas.width,
        webglCanvas.height,
        destW,
        destH,
      );
      ctx.drawImage(webglCanvas, sx, sy, sw, sh, 0, 0, destW, destH);
      if (isCanvasFrameUsable(ctx, canvas)) return true;
      // Tizen Consumer 9.0: WebGL retornou frame ~99% preto. Cai pros fallbacks.
    } catch (err) {
      console.warn('[captureScreen] drawImage(webglCanvas) falhou:', err);
      // Cai pros fallbacks
    }
  }

  // 3) rVFC + createImageBitmap — kept como fallback. Em Tizen Consumer 9.0
  //    a API existe mas o callback nunca dispara (timeout). Em outros engines
  //    modernos (Chrome desktop ≥83) funciona normal.
  const rVFCResult = await captureVideoFrameViaRVFC(video);
  if (rVFCResult.bitmap) {
    try {
      const { sx, sy, sw, sh } = computeObjectFitCover(
        rVFCResult.bitmap.width,
        rVFCResult.bitmap.height,
        destW,
        destH,
      );
      ctx.drawImage(rVFCResult.bitmap, sx, sy, sw, sh, 0, 0, destW, destH);
      rVFCResult.bitmap.close();
      if (isCanvasFrameUsable(ctx, canvas)) return true;
      // Frame preto — cai pro fallback drawImage
    } catch (err) {
      rVFCResult.bitmap.close();
      console.warn('[captureScreen] drawImage(bitmap) falhou:', err);
      // Cai no fallback abaixo
    }
  }

  // 4) Fallback drawImage(video). Em Tizen Consumer 9.0 retorna preto (HW
  //    overlay) mas em Android/browser sempre funciona.
  try {
    const { sx, sy, sw, sh } = computeObjectFitCover(
      video.videoWidth,
      video.videoHeight,
      destW,
      destH,
    );
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, destW, destH);
    // Última verificação: se mesmo aqui veio preto, retorna false pro Canvas
    // Bridge não ativar e o overlay legacy de loading aparecer no lugar.
    return isCanvasFrameUsable(ctx, canvas);
  } catch (err) {
    console.warn('[captureScreen] drawImage(video) fallback falhou:', err);
    return false;
  }
}

/**
 * Tenta capturar o frame corrente do vídeo via
 * {@code HTMLVideoElement.requestVideoFrameCallback} + {@code createImageBitmap}.
 *
 * <p>Em Tizen Consumer 9.0+ (e webOS modernos), o {@code <video>} é renderizado
 * num HW overlay plane separado do compositor HTML. {@code ctx.drawImage(video)}
 * é aceito sem exception, mas o pixel readback do canvas pega o plane vazio
 * (preto puro). O rVFC entrega o frame decoded num callback de scope onde o
 * {@code createImageBitmap(video)} consegue capturar uma cópia CPU-side
 * do frame — essa cópia é livre do HW overlay e pode ser desenhada no canvas
 * com readback funcional.</p>
 *
 * <p>Disponibilidade: Chrome ≥83, Edge ≥83, Tizen 9.0 (Chromium 108+),
 * webOS 4.0+. Em Tizen 7.x e engines antigos retorna {@code null} e o caller
 * cai no caminho legado.</p>
 *
 * @returns ImageBitmap (caller deve chamar {@code .close()} após uso) ou
 *   um motivo de skip pra telemetria
 */
async function captureVideoFrameViaRVFC(
  video: HTMLVideoElement,
): Promise<
  | { bitmap: ImageBitmap }
  | { bitmap: null; reason: 'unsupported' | 'timeout' | 'create-bitmap-error' }
> {
  const videoAny = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (
      cb: (now: number, metadata: unknown) => void,
    ) => number;
  };
  if (typeof videoAny.requestVideoFrameCallback !== 'function') {
    return { bitmap: null, reason: 'unsupported' };
  }
  if (typeof createImageBitmap !== 'function') {
    return { bitmap: null, reason: 'unsupported' };
  }

  return new Promise((resolve) => {
    let resolved = false;
    const timeoutId = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      resolve({ bitmap: null, reason: 'timeout' });
    }, RVFC_TIMEOUT_MS);

    try {
      videoAny.requestVideoFrameCallback!(async () => {
        if (resolved) return;
        try {
          const bitmap = await createImageBitmap(video);
          if (resolved) {
            bitmap.close();
            return;
          }
          resolved = true;
          clearTimeout(timeoutId);
          resolve({ bitmap });
        } catch {
          if (resolved) return;
          resolved = true;
          clearTimeout(timeoutId);
          resolve({ bitmap: null, reason: 'create-bitmap-error' });
        }
      });
    } catch {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      resolve({ bitmap: null, reason: 'create-bitmap-error' });
    }
  });
}

/**
 * Desenha o frame corrente do vídeo replicando {@code object-fit: cover}
 * (preserva aspect ratio, cropa o excesso central).
 *
 * <p>Tenta dois caminhos em ordem:</p>
 * <ol>
 *   <li><b>requestVideoFrameCallback + createImageBitmap</b> — funciona em
 *       Tizen 9.0+ Consumer onde o HW overlay bloqueia o readback do canvas
 *       quando se desenha {@code drawImage(video)} direto. ImageBitmap é
 *       CPU-side, então o canvas consegue ler. Caminho preferido.</li>
 *   <li><b>{@code ctx.drawImage(video, sx, sy, ...)}</b> — fallback legado
 *       que funciona em Tizen 7.x, webOS antigo, navegadores sem rVFC.</li>
 * </ol>
 *
 * <p>Usa {@code source-over} (default) — desenha POR CIMA do que já está no
 * canvas. Tentamos antes com {@code destination-over} pra preservar overlays
 * HTML no topo, mas o {@code Container} do Stream tem
 * {@code background: #000000} opaco. Aceitável: o admin precisa ver o
 * conteúdo do vídeo, não os badges do player.</p>
 *
 * @returns método usado e razão se rVFC foi skipped
 * @throws SecurityError quando o vídeo está protegido por DRM/CORS no fallback
 */
async function drawVideoWithObjectFitCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
): Promise<{
  method: 'ImageCapture' | 'WebGL' | 'rVFC' | 'drawImage';
  rVFCSkipReason?: 'unsupported' | 'timeout' | 'create-bitmap-error';
  webGLSkipReason?:
    | 'unsupported'
    | 'init-failed'
    | 'tex-image-failed'
    | 'zero-size';
  imageCaptureSkipReason?:
    | 'unsupported'
    | 'no-tracks'
    | 'grab-failed'
    | 'capturestream-failed';
}> {
  let webGLSkipReason:
    | 'unsupported'
    | 'init-failed'
    | 'tex-image-failed'
    | 'zero-size'
    | undefined;
  let imageCaptureSkipReason:
    | 'unsupported'
    | 'no-tracks'
    | 'grab-failed'
    | 'capturestream-failed'
    | undefined;

  // 1) Caminho ImageCapture + captureStream — pega frame direto do decoder
  //    pipeline, ANTES do HW overlay compositor que blinda os outros métodos
  //    em Tizen Consumer 9.0.
  const imageCapResult = await captureVideoFrameViaImageCapture(video);
  if (imageCapResult.bitmap) {
    try {
      const { sx, sy, sw, sh } = computeObjectFitCover(
        imageCapResult.bitmap.width,
        imageCapResult.bitmap.height,
        destW,
        destH,
      );
      ctx.drawImage(
        imageCapResult.bitmap,
        sx,
        sy,
        sw,
        sh,
        destX,
        destY,
        destW,
        destH,
      );
      imageCapResult.bitmap.close();
      return { method: 'ImageCapture' };
    } catch {
      imageCapResult.bitmap.close();
      // Cai pros fallbacks
    }
  } else {
    imageCaptureSkipReason = imageCapResult.reason;
  }

  // 2) Caminho WebGL — confirmado que retorna preto em Tizen Consumer 9.0
  //    (HW overlay também blinda esse path). Funciona em Android/browser.
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    webGLSkipReason = 'zero-size';
  } else {
    const webglCanvas = captureVideoFrameViaWebGL(video);
    if (webglCanvas) {
      try {
        const { sx, sy, sw, sh } = computeObjectFitCover(
          webglCanvas.width,
          webglCanvas.height,
          destW,
          destH,
        );
        ctx.drawImage(webglCanvas, sx, sy, sw, sh, destX, destY, destW, destH);
        return { method: 'WebGL', imageCaptureSkipReason };
      } catch {
        // Cai pros fallbacks
        webGLSkipReason = 'tex-image-failed';
      }
    } else {
      // Init failed ou WebGL unsupported
      webGLSkipReason = webglInitFailed ? 'init-failed' : 'tex-image-failed';
    }
  }

  // 3) Caminho rVFC — passa pelo HW overlay do Tizen 9.0 quando a API
  //    funciona (raro em Tizen Consumer; ok em Chrome desktop)
  const rVFCResult = await captureVideoFrameViaRVFC(video);
  if (rVFCResult.bitmap) {
    try {
      const { sx, sy, sw, sh } = computeObjectFitCover(
        rVFCResult.bitmap.width,
        rVFCResult.bitmap.height,
        destW,
        destH,
      );
      ctx.drawImage(
        rVFCResult.bitmap,
        sx,
        sy,
        sw,
        sh,
        destX,
        destY,
        destW,
        destH,
      );
      rVFCResult.bitmap.close();
      return {
        method: 'rVFC',
        webGLSkipReason,
        imageCaptureSkipReason,
      };
    } catch {
      rVFCResult.bitmap.close();
      // Cai no fallback abaixo
    }
  }

  // 4) Fallback: drawImage(video) direto
  const { sx, sy, sw, sh } = computeObjectFitCover(
    video.videoWidth,
    video.videoHeight,
    destW,
    destH,
  );
  // source-over (default): desenha POR CIMA do fundo preto do Container.
  ctx.drawImage(video, sx, sy, sw, sh, destX, destY, destW, destH);

  return {
    method: 'drawImage',
    rVFCSkipReason:
      'reason' in rVFCResult ? rVFCResult.reason : 'create-bitmap-error',
    webGLSkipReason,
    imageCaptureSkipReason,
  };
}

/**
 * Último estágio da cadeia de captura de vídeo: após esgotar readback ao
 * vivo (ou pulá-lo no Tizen), tenta snapshot lógico autenticado; se falhar, placeholder.
 */
async function applyVideoCaptureFallback(
  ctx: CanvasRenderingContext2D,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
  video: HTMLVideoElement,
  vdiag: VideoDiagnostic,
  diagnostics: CaptureDiagnostics,
  opts: {
    placeholderKind:
      | 'placeholder-hw-overlay'
      | 'placeholder-error'
      | 'placeholder-not-capturable';
  },
): Promise<void> {
  void destX;
  void destY;
  void destW;
  void destH;
  const logical = await composeLogicalSnapshotFullFrame(ctx, video);
  if (logical.playbackContext) {
    diagnostics.playbackContext = logical.playbackContext;
  }
  if (logical.ok) {
    vdiag.drawResult = 'logical-snapshot-fallback';
    vdiag.frameUrl = logical.frameUrl;
    vdiag.frameFromCache = logical.fromCache;
    return;
  }

  drawVideoPlaceholder(ctx, destX, destY, destW, destH, video);
  vdiag.drawResult = opts.placeholderKind;
}

function drawVideoPlaceholder(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  video: HTMLVideoElement,
): void {
  ctx.save();
  // source-over (default) pra ficar VISÍVEL — antes usávamos destination-over
  // pra ficar abaixo de overlays HTML, mas o fundo preto opaco do Container do
  // Stream cobria tudo. Vide comentário em drawVideoWithObjectFitCover.

  // Fundo cinza escuro com leve gradiente vertical pra parecer "intencional"
  const gradient = ctx.createLinearGradient(x, y, x, y + h);
  gradient.addColorStop(0, '#2a2a2a');
  gradient.addColorStop(1, '#1a1a1a');
  ctx.fillStyle = gradient;
  ctx.fillRect(x, y, w, h);

  // Ícone "play" + label central
  ctx.fillStyle = '#888';
  const fontSize = Math.max(12, Math.min(h * 0.05, 24));
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  let label: string;
  if (video.paused) {
    label = '⏸ Vídeo pausado';
  } else if (video.readyState < 2) {
    label = '⏳ Carregando vídeo…';
  } else if (video.videoWidth > 0 && video.videoHeight > 0) {
    label = `▶ Vídeo ${video.videoWidth}×${video.videoHeight} (HW overlay)`;
  } else {
    label = '▶ Vídeo (captura indisponível)';
  }
  ctx.fillText(label, x + w / 2, y + h / 2);

  ctx.restore();
}
